/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

export const QUBEPRO_FLOATING_LAYOUT_CHANGE_EVENT =
  'qubepro:floating-layout-change';
export const QUBEPRO_FLOATING_SAFE_LEFT_CSS_VAR =
  '--qp-floating-safe-left';
export const QUBEPRO_FLOATING_SAFE_RIGHT_CSS_VAR =
  '--qp-floating-safe-right';

export interface QubeProFloatingSafeArea {
  left: number;
  right: number;
}

const safeAreaSources = new Map<string, QubeProFloatingSafeArea>();
let publishedSafeArea: QubeProFloatingSafeArea = { left: 0, right: 0 };

function normalizeInset(value: unknown): number {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.max(0, Math.round(numeric)) : 0;
}

function resolveCombinedSafeArea(): QubeProFloatingSafeArea {
  let left = 0;
  let right = 0;
  safeAreaSources.forEach((safeArea) => {
    left = Math.max(left, safeArea.left);
    right = Math.max(right, safeArea.right);
  });
  return { left, right };
}

function publishCombinedSafeArea(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  const next = resolveCombinedSafeArea();
  const unchanged =
    next.left === publishedSafeArea.left && next.right === publishedSafeArea.right;
  const root = document.documentElement;

  if (next.left > 0) {
    root.style.setProperty(QUBEPRO_FLOATING_SAFE_LEFT_CSS_VAR, `${next.left}px`);
  } else {
    root.style.removeProperty(QUBEPRO_FLOATING_SAFE_LEFT_CSS_VAR);
  }
  if (next.right > 0) {
    root.style.setProperty(QUBEPRO_FLOATING_SAFE_RIGHT_CSS_VAR, `${next.right}px`);
  } else {
    root.style.removeProperty(QUBEPRO_FLOATING_SAFE_RIGHT_CSS_VAR);
  }

  if (unchanged) return;
  publishedSafeArea = next;
  window.dispatchEvent(
    new CustomEvent<QubeProFloatingSafeArea>(QUBEPRO_FLOATING_LAYOUT_CHANGE_EVENT, {
      detail: next,
    }),
  );
}

export function setQubeProFloatingSafeArea(
  source: string,
  safeArea: QubeProFloatingSafeArea | null,
): void {
  const normalizedSource = String(source || '').trim();
  if (!normalizedSource) return;

  if (safeArea) {
    safeAreaSources.set(normalizedSource, {
      left: normalizeInset(safeArea.left),
      right: normalizeInset(safeArea.right),
    });
  } else {
    safeAreaSources.delete(normalizedSource);
  }
  publishCombinedSafeArea();
}

function readCssInset(value: string): number {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
}

export function readQubeProFloatingSafeArea(): QubeProFloatingSafeArea {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return { left: 0, right: 0 };
  }
  const styles = window.getComputedStyle(document.documentElement);
  return {
    left: readCssInset(styles.getPropertyValue(QUBEPRO_FLOATING_SAFE_LEFT_CSS_VAR)),
    right: readCssInset(styles.getPropertyValue(QUBEPRO_FLOATING_SAFE_RIGHT_CSS_VAR)),
  };
}
